/**
 * ProtectedImage — Affiche une image protégée par auth
 *
 * Pourquoi : les <img src> ne peuvent pas envoyer de header Authorization.
 * Ce composant fait un fetch() vers /api/media/xxx avec le token,
 * reçoit le fichier, crée un blob URL et l'affiche dans un <img>.
 *
 * Usage legacy : <ProtectedImage src="/uploads/photo.jpeg" alt="…" />
 *   → fetch /api/media/photo.jpeg avec check ownership uid_ filename
 *     (SEC-2). Rejette les Share guests en 403.
 *
 * Usage V1.1 Lot V Fixes A2 (rétrocompatible) :
 *   <ProtectedImage src="/uploads/…" horseId="abc123" slot="photo1" alt="…" />
 *   → fetch /api/horses/abc123/photo/photo1 avec checkHorseAccess
 *     (couvre owner + Share guests acceptés + admins). Corrige le bug
 *     SEC-2 pour les véto / personne_confiance en mode owner_as_vet.
 *
 * Bascule : SI horseId ET slot ∈ {photo1, photo2, photo3} fournis →
 * nouvelle route. SINON → legacy. Aucun breaking change.
 */
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authFetch';

// Cache global des blob URLs pour ne pas re-fetch à chaque render
const blobCache = {};

const ALLOWED_PHOTO_SLOTS = ['photo1', 'photo2', 'photo3'];

function resolveApiPath(src, horseId, slot) {
  if (horseId && slot && ALLOWED_PHOTO_SLOTS.includes(slot)) {
    return `/api/horses/${horseId}/photo/${slot}`;
  }
  return src.replace(/^\/uploads\//, '/api/media/');
}

const ProtectedImage = ({ src, alt, style, onClick, onError, className, horseId, slot }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    // Clé cache distincte selon route — évite collision quand un même src
    // est demandé via legacy ET via route Share-guest-aware.
    const cacheKey = resolveApiPath(src, horseId, slot);

    // Vérifier le cache
    if (blobCache[cacheKey]) {
      setBlobUrl(blobCache[cacheKey]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadImage = async () => {
      try {
        const apiPath = cacheKey;

        const response = await fetch(apiPath, {
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        if (!cancelled) {
          blobCache[cacheKey] = url;
          setBlobUrl(url);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('ProtectedImage load error:', cacheKey, e.message);
          setError(true);
          setLoading(false);
          if (onError) onError(e);
        }
      }
    };

    loadImage();

    return () => { cancelled = true; };
    // onError = callback prop dont l'identité n'est pas garantie par le
    // parent : l'ajouter re-chargerait l'image à chaque render parent.
    // L'effet est keyed sur ses vraies entrées (src/horseId/slot).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, horseId, slot]);

  if (loading) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
        color: '#999',
        fontSize: '12px'
      }}>
        
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fafafa',
        color: '#ccc',
        fontSize: '24px'
      }}>
        
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt || ''}
      style={style}
      onClick={onClick}
      className={className}
    />
  );
};

export default ProtectedImage;
